import { ReactElement, useCallback } from 'react';
import { Box, Button, CSSObject, Typography, lighten } from '@mui/material';
import { styled } from '@mui/material/styles';
import { blueGrey, grey } from '@mui/material/colors';
import { capitalise, isDarkMode, useRoutes } from '@localstack/services';
import { EmulatorType, PlanFamily } from '@localstack/types';
import { TEST_IDS } from '@localstack/constants';

import { getEmulatorPill } from '../../core/subscription/EmulatorPills';

import CloudIcon from './icons/CloudIcon';
import BarChartIcon from './icons/BarChartIcon';
import ArrowUploadProgressIcon from './icons/ArrowUploadProgressIcon';
import StackOverviewIcon from './icons/StackOverviewIcon';
import LocalStateIcon from './icons/LocalStateIcon';
import CardsStarIcon from './icons/CardsStarIcon';
import ControlPointIcon from './icons/ControlPointIcon';
import VpnKeyIcon from './icons/VpnKeyIcon';

type FeatureUnavailableBoxVariant = 'default' | 'bg-centered' | 'fixed-overlay';

const iconMap = {
  cloudpods: CloudIcon,
  stackInsights: BarChartIcon,
  ephemeralInstances: ArrowUploadProgressIcon,
  stackOverview: StackOverviewIcon,
  localState: LocalStateIcon,
  iamStream: CardsStarIcon,
  chaosEngineering: ControlPointIcon,
  ciAuthToken: VpnKeyIcon,
};

const planName: Partial<Record<PlanFamily, string>> = {
  [PlanFamily.BASE_PLANS]: 'Base',
  [PlanFamily.PRO_PLANS]: 'Starter',
  [PlanFamily.ULTIMATE_PLANS]: 'Ultimate',
};

type Props = {
  icon: keyof typeof iconMap;
  title: string;
  description: string;
  /**
   * The plan the feature is sold under, and the emulators it applies to. A lone emulator
   * gets a pill next to the title; several leave it off, since the plan then isn't tied to
   * one product.
   */
  availableIn?: { family: PlanFamily; emulators?: EmulatorType[] };
  docsLink?: string;
  upgradeLink?: string;
  onFeatureUpgrade?: () => void;
  variant?: FeatureUnavailableBoxVariant;
  primaryButtonText?: string;
};

interface ParentContainerProps {
  variant: FeatureUnavailableBoxVariant;
}

const ParentContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'variant' })<ParentContainerProps>(({
  theme,
  variant,
}) => {
  const dark = isDarkMode(theme);
  const baseStyles = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const overlayStyles: CSSObject =
    variant === 'fixed-overlay'
      ? {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(181deg, rgba(217, 217, 217, 0) -50%, ${
            dark ? lighten(theme.palette.background.default, 0.03) : '#F9FAFB'
          } 44.5%)`,
          zIndex: 1,
        }
      : {};

  const bgCenteredStyles: CSSObject =
    variant === 'bg-centered'
      ? {
          textAlign: 'center',
          backgroundColor: dark ? theme.palette.background.default : grey[50],
          border: `1px solid ${dark ? theme.palette.divider : blueGrey[100]}`,
          borderRadius: '5px',
          padding: `${theme.spacing(2)} 0`,
        }
      : {};
  return {
    ...baseStyles,
    ...overlayStyles,
    ...bgCenteredStyles,
  };
});

const UpgradeBox = styled(Box)({
  width: 360,
  padding: '10px 0px',
});

const IconContainer = styled('div')(({ theme }) => ({
  color: isDarkMode(theme) ? theme.palette.text.secondary : grey[700],
  fontSize: 40,
}));

export const FeatureUnavailableBox = ({
  icon,
  title,
  description,
  availableIn,
  docsLink,
  upgradeLink,
  onFeatureUpgrade,
  primaryButtonText,
  variant = 'default',
}: Props): ReactElement => {
  const { goto } = useRoutes();
  const IconComponent = iconMap[icon];

  const handleUpgrade = useCallback(() => {
    if (onFeatureUpgrade) {
      onFeatureUpgrade();
    } else if (upgradeLink) {
      goto(upgradeLink);
    }
  }, [onFeatureUpgrade, upgradeLink]);

  const showUpgradeButton = upgradeLink || onFeatureUpgrade;
  const showCTAContainer = showUpgradeButton || docsLink;

  // If a feature is available in multiple emulators, we don't show a pill next to the title, since the plan then isn't tied to one emulator.
  const pilledEmulator = availableIn?.emulators?.length === 1 ? availableIn.emulators[0] : undefined;

  return (
    <ParentContainer variant={variant}>
      <UpgradeBox data-testid={TEST_IDS.FEATURE_UNAVAILABLE_BOX}>
        <IconContainer as={IconComponent} />
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mt={2}
          justifyContent={variant === 'bg-centered' ? 'center' : undefined}
        >
          <Typography variant="h4">{title}</Typography>
          {/* The box is a fixed 360px, so keep the pill at its natural size and let the title wrap instead. */}
          {pilledEmulator && (
            <Box flexShrink={0} data-testid={TEST_IDS.FEATURE_UNAVAILABLE_EMULATOR_PILL}>
              {getEmulatorPill(pilledEmulator)}
            </Box>
          )}
        </Box>
        <Typography mt={1}>{description}</Typography>
        {showCTAContainer && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mt={2.5}
            justifyContent={variant === 'bg-centered' ? 'center' : undefined}
          >
            {showUpgradeButton && (
              <Button sx={{ textTransform: 'none' }} color="primary" variant="contained" onClick={handleUpgrade}>
                {primaryButtonText ||
                  `Upgrade${availableIn ? ` to ${capitalise(planName[availableIn.family] || availableIn.family)}` : ''}`}
              </Button>
            )}
            {docsLink && (
              <Button variant="outlined" href={docsLink} target="_blank" rel="noopener noreferrer">
                Documentation
              </Button>
            )}
          </Box>
        )}
      </UpgradeBox>
    </ParentContainer>
  );
};
